import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { API } from "@/lib/apiClient";
import { ChevronLeft, ChevronRight, Target, Sparkles, Play, X } from "lucide-react";

const NAVY = "#1a2a6c";
const TEAL = "#00b09b";

function buildSlides(deck) {
  const slides = [];
  slides.push({ type: "title" });
  if (deck.outcomes?.length) slides.push({ type: "outcomes" });
  (deck.lessons || []).forEach((l, i) => slides.push({ type: "lesson", lesson: l, index: i }));
  if (deck.monetization?.length) slides.push({ type: "monetization" });
  if (deck.sales_copy) slides.push({ type: "outro" });
  return slides;
}

export default function PublicDeck() {
  const { pid } = useParams();
  const [deck, setDeck] = useState(null);
  const [error, setError] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api
      .get(`/public/decks/${pid}`)
      .then((r) => setDeck(r.data))
      .catch(() => setError(true));
  }, [pid]);

  // Dynamic social-share preview (title + OG/Twitter tags, cover as og:image).
  useEffect(() => {
    if (!deck) return;
    const prevTitle = document.title;
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };
    const title = `${deck.title || "Mini Course"} — Hook2Income AI`;
    const desc = deck.promise || "A beginner-friendly mini course by Hook2Income AI.";
    document.title = title;
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:type", "website");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", desc);
    if (deck.cover_url) {
      const img = `${API}/public/decks/${pid}/cover`;
      setMeta("property", "og:image", img);
      setMeta("name", "twitter:image", img);
      setMeta("name", "twitter:card", "summary_large_image");
    }
    return () => {
      document.title = prevTitle;
    };
  }, [deck, pid]);

  const slides = useMemo(() => (deck ? buildSlides(deck) : []), [deck]);
  const brandPrimary = deck?.brand?.primary_color || NAVY;
  const brandAccent = deck?.brand?.accent_color || TEAL;
  const accent = brandPrimary;
  const [present, setPresent] = useState(false);
  const stageRef = useRef(null);

  const go = useCallback(
    (d) => setIdx((i) => Math.min(Math.max(i + d, 0), slides.length - 1)),
    [slides.length]
  );

  const enterPresent = async () => {
    setPresent(true);
    try {
      await document.documentElement.requestFullscreen?.();
    } catch (e) {
      console.warn("Fullscreen request failed:", e);
    }
  };
  const exitPresent = async () => {
    setPresent(false);
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
    } catch (e) {
      console.warn("Exit fullscreen failed:", e);
    }
  };

  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement) setPresent(false);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "Escape") setPresent(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [go]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FDFBF7] px-6 text-center">
        <div>
          <div className="mx-auto flex h-12 w-12 items-center justify-center">
            <img src="/logo-mark.png" alt="Hook2Income AI" className="h-12 w-12 object-contain" />
          </div>
          <h1 className="mt-6 font-['Outfit',sans-serif] text-2xl font-bold text-[#1C1917]">Deck not found</h1>
          <p className="mt-2 text-[#57534E]">This shared slide deck is no longer available.</p>
        </div>
      </div>
    );
  }

  if (!deck) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FDFBF7]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-t-transparent" style={{ borderColor: accent, borderTopColor: "transparent" }} />
      </div>
    );
  }

  const slide = slides[idx];
  const presenter = deck.brand?.business_name?.trim();
  const coverSrc = deck.cover_url ? `${API}/public/decks/${pid}/cover` : null;
  const brandLogo = deck.brand?.logo_url ? `${API}/public/decks/${pid}/logo` : null;

  return (
    <div className="flex min-h-screen flex-col bg-[#F4F1EA]" data-testid="public-deck">
      {/* top bar */}
      <header className="flex items-center justify-between px-5 py-4 sm:px-8">
        <div className="flex items-center gap-2.5">
          <img src="/logo-mark.png" alt="Hook2Income AI" className="h-8 w-8 object-contain" />
          <span className="font-['Outfit',sans-serif] text-sm font-bold text-[#1C1917]">
            Hook2Income<span style={{ color: accent }}> AI</span>
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs font-medium text-[#78716C]">
            {idx + 1} / {slides.length}
          </span>
          <button
            onClick={enterPresent}
            data-testid="present-btn"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
            style={{ background: accent }}
          >
            <Play size={15} /> Present
          </button>
        </div>
      </header>

      {/* stage */}
      <main className="flex flex-1 items-center justify-center px-4 pb-4 sm:px-8">
        <div className="relative w-full max-w-5xl">
          <div className="relative aspect-[16/10] w-full overflow-hidden rounded-3xl border border-[#E6E2D8] bg-white shadow-[0_20px_60px_-20px_rgba(28,25,23,0.25)]">
            {/* accent side bar */}
            <div className="absolute inset-y-0 left-0 w-1.5" style={{ background: accent }} />
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="absolute inset-0 overflow-y-auto px-8 py-10 sm:px-16 sm:py-14"
              >
                <SlideContent slide={slide} deck={deck} primary={brandPrimary} accent={brandAccent} presenter={presenter} coverSrc={coverSrc} brandLogo={brandLogo} />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* nav */}
          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={() => go(-1)}
              disabled={idx === 0}
              data-testid="deck-prev"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E6E2D8] bg-white px-4 py-2 text-sm font-medium text-[#44403C] transition-colors hover:bg-[#FDFBF7] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft size={16} /> Prev
            </button>

            <div className="flex items-center gap-1.5">
              {slides.map((_, i) => (
                <button
                  key={`deck-dot-${i}`}
                  onClick={() => setIdx(i)}
                  data-testid={`deck-dot-${i}`}
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: i === idx ? 24 : 8,
                    background: i === idx ? accent : "#D6D3D1",
                  }}
                />
              ))}
            </div>

            <button
              onClick={() => go(1)}
              disabled={idx === slides.length - 1}
              data-testid="deck-next"
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40"
              style={{ background: accent }}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </main>

      <footer className="py-5 text-center text-xs text-[#78716C]">
        Made with{" "}
        <span className="font-semibold text-[#1C1917]">Hook2Income AI</span>
      </footer>

      {/* Presentation Mode overlay */}
      {present && (
        <div
          ref={stageRef}
          data-testid="present-overlay"
          className="fixed inset-0 z-50 flex flex-col bg-[#F4F1EA]"
        >
          {/* minimal top chrome */}
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="Hook2Income AI" className="h-7 w-7 object-contain" />
              <span className="font-['Outfit',sans-serif] text-xs font-bold text-[#1C1917]">
                Hook2Income<span style={{ color: accent }}> AI</span>
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs font-medium text-[#78716C]">{idx + 1} / {slides.length}</span>
              <button onClick={exitPresent} data-testid="present-exit" title="Exit (Esc)"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1C1917]/5 text-[#57534E] transition-colors hover:bg-[#1C1917]/10">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* slide stage */}
          <div className="relative flex flex-1 items-center justify-center px-4 pb-6 sm:px-12">
            {/* click zones */}
            <button aria-label="Previous" onClick={() => go(-1)} disabled={idx === 0}
              className="absolute left-0 top-0 z-10 h-full w-1/4 cursor-w-resize disabled:cursor-default" />
            <button aria-label="Next" onClick={() => go(1)} disabled={idx === slides.length - 1}
              className="absolute right-0 top-0 z-10 h-full w-1/4 cursor-e-resize disabled:cursor-default" />

            <div className="relative aspect-[16/9] max-h-full w-full max-w-6xl overflow-hidden rounded-2xl border border-[#E6E2D8] bg-white shadow-[0_20px_60px_-20px_rgba(28,25,23,0.3)]">
              <div className="absolute inset-y-0 left-0 w-1.5" style={{ background: accent }} />
              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="absolute inset-0 overflow-y-auto px-10 py-12 sm:px-20 sm:py-16"
                >
                  <SlideContent slide={slide} deck={deck} primary={brandPrimary} accent={brandAccent} presenter={presenter} coverSrc={coverSrc} brandLogo={brandLogo} big />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* bottom controls */}
          <div className="flex items-center justify-center gap-4 pb-8">
            <button onClick={() => go(-1)} disabled={idx === 0} data-testid="present-prev"
              className="flex h-12 w-12 items-center justify-center rounded-full border border-[#E6E2D8] bg-white text-[#44403C] shadow-sm transition-transform hover:-translate-y-0.5 disabled:opacity-40">
              <ChevronLeft size={22} />
            </button>
            <div className="flex items-center gap-1.5">
              {slides.map((_, i) => (
                <span key={`deck-bar-${i}`} className="h-1.5 rounded-full transition-all"
                  style={{ width: i === idx ? 22 : 6, background: i === idx ? accent : "#D6D3D1" }} />
              ))}
            </div>
            <button onClick={() => go(1)} disabled={idx === slides.length - 1} data-testid="present-next"
              className="flex h-12 w-12 items-center justify-center rounded-full text-white shadow-sm transition-transform hover:-translate-y-0.5 disabled:opacity-40"
              style={{ background: accent }}>
              <ChevronRight size={22} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SlideContent({ slide, deck, accent, primary, presenter, coverSrc, brandLogo, big }) {
  const h1 = big
    ? "mt-4 font-['Outfit',sans-serif] text-5xl font-bold leading-tight sm:text-7xl"
    : "mt-4 font-['Outfit',sans-serif] text-4xl font-bold leading-tight sm:text-6xl";
  const h2 = big
    ? "font-['Outfit',sans-serif] text-4xl font-bold sm:text-5xl"
    : "font-['Outfit',sans-serif] text-3xl font-bold sm:text-4xl";

  if (slide.type === "title") {
    return (
      <div className="flex h-full flex-col justify-center gap-8 sm:flex-row sm:items-center" data-testid="slide-title">
        <div className={coverSrc ? "sm:flex-1" : "w-full"}>
          {brandLogo && <img src={brandLogo} alt="Brand logo" data-testid="deck-brand-logo" className={`mb-6 w-auto object-contain ${big ? "h-16" : "h-12"}`} />}
          <span className="text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: accent }}>Mini Course</span>
          <h1 className={h1} style={{ color: primary }}>{deck.title}</h1>
          {deck.promise && <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#57534E] sm:text-xl">{deck.promise}</p>}
          <p className="mt-10 text-sm text-[#78716C]">
            {presenter ? `Presented by ${presenter}` : "A Hook2Income AI presentation"}
          </p>
        </div>
        {coverSrc && (
          <div className={`shrink-0 ${big ? "sm:w-2/5" : "sm:w-1/3"}`}>
            <img
              src={coverSrc}
              alt="Course cover"
              data-testid="deck-cover-image"
              className="aspect-square w-full rounded-2xl object-cover shadow-[0_12px_40px_-12px_rgba(28,25,23,0.35)]"
              style={{ border: `3px solid ${primary}` }}
            />
          </div>
        )}
      </div>
    );
  }
  if (slide.type === "outcomes") {
    return (
      <div className="flex h-full flex-col justify-center" data-testid="slide-outcomes">
        <h2 className={h2} style={{ color: primary }}>What you'll achieve</h2>
        <ul className="mt-8 space-y-4">
          {deck.outcomes.map((o, i) => (
            <li key={`${o}-${i}`} className="flex items-start gap-3 text-lg text-[#44403C]">
              <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full" style={{ background: `${accent}22`, color: accent }}>
                <Target size={14} />
              </span>
              {o}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (slide.type === "lesson") {
    return (
      <div data-testid={`slide-lesson-${slide.index}`}>
        <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: accent }}>Lesson {slide.index + 1}</span>
        <h2 className={`mt-3 ${h2}`} style={{ color: primary }}>{slide.lesson.title}</h2>
        {slide.lesson.script && <p className="mt-6 whitespace-pre-wrap text-base leading-relaxed text-[#57534E]">{slide.lesson.script}</p>}
        <div className="mt-6 space-y-3">
          {[["Exercise", slide.lesson.exercise], ["Worksheet", slide.lesson.worksheet], ["Reflection", slide.lesson.reflection]]
            .filter(([, v]) => v)
            .map(([label, v]) => (
              <div key={label} className="rounded-xl border border-[#E6E2D8] bg-[#FDFBF7] p-4">
                <p className="text-xs font-bold uppercase tracking-wide" style={{ color: accent }}>{label}</p>
                <p className="mt-1 text-sm leading-relaxed text-[#44403C]">{v}</p>
              </div>
            ))}
        </div>
      </div>
    );
  }
  if (slide.type === "monetization") {
    return (
      <div data-testid="slide-monetization">
        <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: accent }}>Turn it into income</span>
        <h2 className={`mt-3 ${h2}`} style={{ color: primary }}>Monetization Strategy</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {deck.monetization.map((m, i) => (
            <div key={`${m.model || "model"}-${i}`} className="rounded-2xl border border-[#E6E2D8] bg-[#FDFBF7] p-5">
              <p className="text-xs font-bold uppercase tracking-wide" style={{ color: accent }}>{m.model}</p>
              <p className="mt-1 font-['Outfit',sans-serif] text-xl font-bold" style={{ color: primary }}>{m.price}</p>
              <p className="mt-2 text-sm leading-relaxed text-[#57534E]">{m.strategy}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (slide.type === "outro") {
    return (
      <div className="flex h-full flex-col justify-center" data-testid="slide-outro">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: `${accent}22`, color: accent }}>
          <Sparkles size={24} />
        </div>
        <h2 className={`mt-6 ${h2}`} style={{ color: primary }}>Ready to go further?</h2>
        <p className="mt-5 max-w-2xl whitespace-pre-wrap text-lg leading-relaxed text-[#57534E]">{deck.sales_copy}</p>
      </div>
    );
  }
  return null;
}
